import React, { useEffect, useRef } from "react";

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  subtitle,
  message, 
  confirmText = "Bestätigen",
  cancelText = "Abbrechen",
  type = "danger",
  layout = "inline",
  isLoading = false,
  disabled = false
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen || isLoading) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isLoading, onClose]);

  useEffect(() => {
    if (!isOpen || !panelRef.current) return;
    const cancelBtn = panelRef.current.querySelector('[data-confirmation-cancel]');
    if (cancelBtn) cancelBtn.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const getIcon = () => {
    switch (type) {
      case "danger":
        return "fas fa-exclamation-triangle";
      case "warning":
        return "fas fa-exclamation-circle";
      case "info":
        return "fas fa-info-circle";
      default:
        return "fas fa-question-circle";
    }
  };

  const getIconColor = () => {
    switch (type) {
      case "danger":
        return "text-red-600 dark:text-red-400";
      case "warning":
        return "text-amber-600 dark:text-amber-400";
      case "info":
        return "text-blue-600 dark:text-blue-400";
      default:
        return "text-slate-600 dark:text-slate-400";
    }
  };

  const getIconBgColor = () => {
    switch (type) {
      case "danger":
        return "bg-red-100 dark:bg-red-900/35";
      case "warning":
        return "bg-amber-100 dark:bg-amber-900/35";
      case "info":
        return "bg-blue-100 dark:bg-blue-900/35";
      default:
        return "bg-slate-100 dark:bg-slate-700";
    }
  };

  const getConfirmButtonClass = () => {
    switch (type) {
      case "danger":
        return "bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500 text-white border border-transparent shadow-sm";
      case "warning":
        return "bg-amber-600 hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-500 text-white border border-transparent shadow-sm";
      case "info":
        return "bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white border border-transparent shadow-sm";
      default:
        return "bg-slate-700 hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500 text-white border border-transparent shadow-sm";
    }
  };

  const titleId = "confirmation-modal-title";
  const descId = message ? "confirmation-modal-desc" : undefined;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 min-h-[100dvh] bg-black/50 backdrop-blur-sm transition-opacity"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="relative w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 sm:p-6">
          {layout === "inline" ? (
            <>
              <div className="flex gap-4 mb-4">
                <div
                  className={`flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 ${getIconBgColor()} rounded-xl flex items-center justify-center`}
                  aria-hidden="true"
                >
                  <i className={`${getIcon()} ${getIconColor()} text-lg sm:text-xl`}></i>
                </div>
                <div className="min-w-0 pt-0.5">
                  <h3
                    id={titleId}
                    className="text-base sm:text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100"
                  >
                    {title}
                  </h3>
                  {subtitle && (
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              {message && (
                <p
                  id={descId}
                  className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed pl-0 sm:pl-[3.75rem]"
                >
                  {message}
                </p>
              )}
            </>
          ) : (
            <>
              <div className="flex flex-col items-center text-center mb-2">
                <div
                  className={`w-12 h-12 ${getIconBgColor()} rounded-xl flex items-center justify-center mb-4`}
                  aria-hidden="true"
                >
                  <i className={`${getIcon()} ${getIconColor()} text-xl`}></i>
                </div>
                <h3
                  id={titleId}
                  className="text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100"
                >
                  {title}
                </h3>
              </div>
              {subtitle && (
                <p className="text-center text-sm text-slate-500 dark:text-slate-400 mb-2">
                  {subtitle}
                </p>
              )}
              {message && (
                <p
                  id={descId}
                  className="text-center text-sm text-slate-600 dark:text-slate-300 leading-relaxed py-3"
                >
                  {message}
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 px-5 sm:px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-700/40">
          <button 
            type="button"
            data-confirmation-cancel
            className="inline-flex flex-1 items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button 
            type="button"
            className={`inline-flex flex-1 items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${getConfirmButtonClass()}`}
            onClick={handleConfirm}
            disabled={isLoading || disabled}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin text-xs" aria-hidden="true"></i>
                {confirmText}
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
